import React, {useEffect, useRef, useState} from "react";
import {
  Box, ButtonBase, IconButton, InputBase, MenuItem, Stack, SvgIcon, TextField, Tooltip, Typography, useMediaQuery, useTheme
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Call02Icon} from "@hugeicons/core-free-icons";
import {useWebrtc} from "../../context/WebrtcContext";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {subProjectLabel} from "../../utils/subProjects";

// a phone number: optional leading +, then digits (spaces, dashes, dots and brackets are ignored)
const normalize = (value) => String(value || "").trim().replace(/[\s\-().]/g, "");
const isPhoneNumber = (value) => /^\+?\d{3,15}$/.test(normalize(value));

const outboundOf = (subProject) => subProject?.coreConfiguration?.outbound_call || "";

// digit, letters under it; a long press on 0 types the leading +
const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"], ["4", "GHI"], ["5", "JKL"], ["6", "MNO"], ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];
const LONG_PRESS_MS = 500;

const keySx = {
  width: 64, height: 64, borderRadius: "50%", flexDirection: "column", color: "#23264D", bgcolor: "#FAFAFD",
  "&:hover": {bgcolor: "#ECECF3"}, "&:active": {bgcolor: "rgba(132, 145, 247, 0.14)"},
};

const BackspaceIcon = () => (
  <SvgIcon sx={{fontSize: 22}} viewBox={"0 0 24 24"}>
    <path fill={"none"} stroke={"currentColor"} strokeWidth={1.6} strokeLinecap={"round"} strokeLinejoin={"round"}
          d={"M9 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6-7 6-7zM13 10l4 4m0-4-4 4"}/>
  </SvgIcon>
);

// Dial pad of the phone panel: this browser rings first (answered automatically), then the number is dialed from the
// chosen sub project's outbound caller ID, and the two are bridged once it answers.
export const PhoneDialTab = () => {
  const {project, subProject} = useRegistryProject();
  const {status, call, callCustomer} = useWebrtc();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const subProjects = project?.subProjects || [];
  const [number, setNumber] = useState("");
  const [subProjectId, setSubProjectId] = useState("");
  const [touched, setTouched] = useState(false);
  const longPressRef = useRef({timer: null, fired: false});

  useEffect(() => {
    setSubProjectId(String(subProject?.id || subProjects[0]?.id || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subProject?.id]);

  useEffect(() => () => clearTimeout(longPressRef.current.timer), []);

  const chosen = subProjects.find(item => String(item.id) === String(subProjectId)) || subProject;
  const valid = isPhoneNumber(number);
  const connected = status === "registered";
  const press = (key) => {
    setTouched(false);
    setNumber(current => current + key);
  };

  const startLongPress = (key) => {
    if (key !== "0") return;
    longPressRef.current.fired = false;
    longPressRef.current.timer = setTimeout(() => {
      longPressRef.current.fired = true;
      press("+");
    }, LONG_PRESS_MS);
  };
  const endLongPress = () => clearTimeout(longPressRef.current.timer);
  const onKeyClick = (key) => {
    if (longPressRef.current.fired) {
      longPressRef.current.fired = false;
      return;
    }
    press(key);
  };

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !connected || call) return;
    callCustomer({projectId: project?.id, subProjectId: subProjectId || undefined, phoneNumber: normalize(number)});
    setNumber("");
    setTouched(false);
  };

  return (
    <Stack component={"form"} onSubmit={submit} sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, py: 1.5}}>
      <Typography sx={{
        fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textAlign: "center", textTransform: "uppercase",
        color: outboundOf(chosen) ? "#8A8AA0" : "#C73A3A",
      }}>
        {outboundOf(chosen) ? `Calling as ${outboundOf(chosen)}` : "No outbound caller ID"}
      </Typography>
      <InputBase
        // on phones the keyboard would cover the dial pad
        autoFocus={!isMobile} fullWidth placeholder={"+389 70 123 456"} value={number}
        onChange={(e) => setNumber(e.target.value)}
        inputProps={{inputMode: "tel", autoComplete: "tel", "aria-label": "Phone number", style: {textAlign: "center"}}}
        sx={{mt: 0.75, fontSize: 28, fontWeight: 500, letterSpacing: "0.01em", color: "#23264D"}}
      />
      <Typography sx={{minHeight: 18, fontSize: 12, textAlign: "center", color: "#C73A3A"}}>
        {touched && !valid ? "Enter a phone number, digits with an optional leading +"
          : !outboundOf(chosen) ? "Set one in Settings → Outbound Caller ID" : ""}
      </Typography>

      {subProjects.length > 1 && (
        <TextField
          select fullWidth size={"small"} label={"Call from"} value={subProjectId}
          onChange={(e) => setSubProjectId(e.target.value)}
          sx={{mt: 1, "& .MuiOutlinedInput-root": {borderRadius: "12px", fontSize: 14}}}
        >
          {subProjects.map(item => (
            <MenuItem key={item.id} value={String(item.id)} sx={{fontSize: 14}}>{subProjectLabel(item)}</MenuItem>
          ))}
        </TextField>
      )}

      <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", justifyItems: "center", alignItems: "center", rowGap: 1.25, mt: 1.5, mb: 1}}>
        {KEYS.map(([key, letters]) => (
          <ButtonBase
            key={key} type={"button"} aria-label={`Dial ${key}`} sx={keySx}
            onClick={() => onKeyClick(key)}
            onPointerDown={() => startLongPress(key)} onPointerUp={endLongPress} onPointerLeave={endLongPress}
          >
            <Typography component={"span"} sx={{fontSize: 24, fontWeight: 500, lineHeight: 1}}>{key}</Typography>
            <Typography component={"span"} sx={{fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", lineHeight: 1.4, color: "#8A8AA0", minHeight: 12}}>
              {letters}
            </Typography>
          </ButtonBase>
        ))}
        <span/>
        <Tooltip title={call ? "You are already on a call" : "Call"}>
          <span>
            <IconButton
              type={"submit"} aria-label={"Call"} disabled={!connected || Boolean(call)}
              sx={{
                width: 64, height: 64, color: "#fff", background: "linear-gradient(145deg, #6FE39F, #2FC275)", boxShadow: "0 8px 24px rgba(47, 194, 117, 0.4)",
                "&:hover": {background: "linear-gradient(145deg, #62D893, #27B56A)"},
                "&.Mui-disabled": {color: "#fff", background: "#C9CAD8", boxShadow: "none"},
              }}
            >
              <HugeiconsIcon icon={Call02Icon} size={26}/>
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={"Delete the last digit"}>
          <span>
            <IconButton
              aria-label={"Delete the last digit"} disabled={!number} onClick={() => setNumber(current => current.slice(0, -1))}
              sx={{width: 48, height: 48, color: "#6B6B80", "&:hover": {bgcolor: "#FAFAFD"}}}
            >
              <BackspaceIcon/>
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {!connected && (
        <Typography sx={{fontSize: 12, textAlign: "center", color: "#C73A3A"}}>Your browser phone is not connected.</Typography>
      )}
    </Stack>
  );
};
